import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { useCallbackRef, usePortal } from '../../../hooks';
import { EMPTY_ARRAY } from '../../../utils';
import { Box, HStack, VStack } from '../../Core';
import { Tab, TabAppearance, TabList, TabSize, useAddTabPopover } from '../../Tabs';
import type { useEntityAdmin } from '../useEntityAdmin';
import { useEntityAdminTabsContext } from '../wrappers/EntityAdminTabsWrapper';

export const EntityAdminTabsAndFilters = memo(
  ({ filtersPortalId }: { filtersPortalId: ReturnType<typeof useEntityAdmin>['filtersPortalId'] }) => {
    const { setPortalRef: filtersContainerRef } = usePortal(filtersPortalId);
    const tabsContext = useEntityAdminTabsContext();

    //Build the tab type options by filtering out tabs that don't have a tabType or label
    const tabTypeOptions = useMemo(() => {
      return (
        tabsContext?.defaultInitialItems
          .filter(tab => tab.tabType && tab.label)
          .map(tab => {
            return { label: tab.label!, tabType: tab.tabType! };
          }) ?? EMPTY_ARRAY
      );
    }, [tabsContext?.defaultInitialItems]);

    const handleSelectTabType = useCallbackRef((item: { label: string; tabType: string }) =>
      tabsContext?.onAdd({ label: item.label, tabType: item.tabType })
    );

    const { renderAddTab } = useAddTabPopover<{ label: string; tabType: string }>({
      options: tabTypeOptions,
      getLabel: item => item.label,
      onSelection: handleSelectTabType,
      appearance: TabAppearance.Filled,
      size: TabSize.Small,
    });

    return (
      <VStack w="100%">
        <HStack
          w="100%"
          h="40px" // Match tabs height
          pl="spacing12"
          pr="spacing24"
          background="backgroundContent"
          justifyContent="space-between"
        >
          <HStack w="100%">
            {tabsContext != null && (
              <TabList
                w="100%"
                overflow="hidden"
                px={0}
                renderAddTab={tabsContext.defaultInitialItems.length > 1 ? renderAddTab : undefined}
              >
                {tabsContext.tabItems.map(item => (
                  <Tab key={item.id} {...item} />
                ))}
              </TabList>
            )}
          </HStack>
          <FiltersContainerBox ref={filtersContainerRef} />
        </HStack>
      </VStack>
    );
  }
);

const FiltersContainerBox = styled(Box)`
  @media (max-width: 576px) {
    display: none;
  }
`;
